import { countBy, toPairs } from 'lodash-es';
import { SharedSessionStorage } from '@hotjar/webapp/utils/shared-session-storage';
import { log } from '@hotjar/insights-common/lib/log';
import { tagRecording, trigger } from '@hotjar/webapp/tracking/tools/hotjar';

/**
 * * A simple service to track number of watched recordings in session and set trigger if value reaches 10
 * */
export class RecordingPlaybackTrackerCtor {
  private NUMBER_OF_RECORDINGS_BEFORE_TRIGGER = 10;

  private triggered = false;

  /**
   * Increment number of watched recordings.
   * */
  // eslint-disable-next-line class-methods-use-this
  public incrementWatched = (): number => {
    let viewedRecs = (SharedSessionStorage.get('viewedRecordings') || 0) as number;
    viewedRecs += 1;

    SharedSessionStorage.set('viewedRecordings', viewedRecs);

    return viewedRecs;
  };

  /**
   * Set trigger if enough recordings have been viewed
   * @param nRecordings number of recordings to view before trigger is set
   */
  public triggerIfEnoughViewed(nRecordings: number): void {
    if (nRecordings < this.NUMBER_OF_RECORDINGS_BEFORE_TRIGGER || this.triggered) {
      return;
    }

    this.triggered = true;
    // We need to use the HotjarTracking service directly as opposed to the Track service, because we load
    // this inside the shared recording player. Otherwise we would need to import all the other
    // dependencies. Also, Track has a lot of side effects on initialization which we wouldn't want to run
    // here.

    log.debug('Hotjar: Executing Javascript Trigger ten_recordings_viewed');

    trigger('ten_recordings_viewed');
  }

  /**
   * Tag recording for every filter used multiple times in recording search queries.
   *
   * The tag will look like:
   * Repeated filter: ${filterName}
   */
  // eslint-disable-next-line class-methods-use-this
  public tagRepeatedFilters = (filters: Array<{ type: string }> | undefined | null): void => {
    if (!filters) {
      return;
    }

    // count filter by type and return json {filter_type: count}
    const counted = countBy(filters, (filterConfig: { type: string }) => filterConfig.type);
    // convert json to array ['filter_type', count]
    const pairs = toPairs(counted);
    // filter only the types where count is bigger than 1
    const filtered = pairs.filter((pair: [string, number]) => pair[1] > 1);
    // remove the count and return the type only
    const mapped = filtered.map((duplicate: [string, number]) => duplicate[0]);

    mapped.forEach((filterName: string) => {
      tagRecording(`Repeated filter: ${filterName}`);
    });
  };
}

export const RecordingPlaybackTracker = new RecordingPlaybackTrackerCtor();
